import { EllipsisIcon } from '@daily/shared/components/Icons';
import { MenuButton } from '@daily/shared/components/Menu';
import { VisuallyHidden } from '@daily/shared/components/VisuallyHidden';
import { useTheme } from '@daily/shared/contexts/Theme';
import { useScreenShare } from '@daily-co/daily-react-hooks';
import classnames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ParticipantMenu } from '../People/ParticipantMenu';

interface Props {
  alignMenu?: React.ComponentProps<typeof ParticipantMenu>['align'];
  isScreen?: boolean;
  sessionId: string;
}

export const TileActions: React.FC<Props> = ({
  alignMenu = 'right',
  isScreen = false,
  sessionId,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [showMenu, setShowMenu] = useState(false);
  const [instance, setInstance] = useState(0);

  const { screens } = useScreenShare();
  const screen = useMemo(
    () => screens.find((s) => s.session_id === sessionId),
    [screens, sessionId]
  );

  const btnId = useMemo(
    () => `p-actions-${isScreen ? screen?.screenId : sessionId}-${instance}`,
    [instance, isScreen, screen?.screenId, sessionId]
  );
  const menuId = useMemo(
    () =>
      `p-actions-menu-${isScreen ? screen?.screenId : sessionId}-${instance}`,
    [instance, isScreen, screen?.screenId, sessionId]
  );

  useEffect(() => {
    if (document.querySelectorAll(`[id="${btnId}"]`).length === 1) return;
    setInstance(Math.ceil(100000 * Math.random()));
  }, [btnId]);

  const handleMenuClick = () => setShowMenu(true);
  const handleMenuClose = () => setShowMenu(false);

  return (
    <div className={classnames('tile-actions', { showMenu })}>
      <MenuButton
        aria-controls={menuId}
        id={btnId}
        onClick={handleMenuClick}
        open={showMenu}
        variant="ghost"
      >
        <EllipsisIcon color={colors.baseText} size={24} />
        <VisuallyHidden>{t('people.showParticipantActions')}</VisuallyHidden>
      </MenuButton>
      {showMenu && (
        <ParticipantMenu
          align={alignMenu}
          aria-labelledby={btnId}
          id={menuId}
          onClose={handleMenuClose}
          isScreen={isScreen}
          sessionId={sessionId}
        />
      )}
      <style jsx>{`
        .tile-actions {
          background: ${colors.background};
          border-radius: 4px;
          box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.04),
            0px 0px 4px rgba(0, 0, 0, 0.04);
          opacity: 0;
          position: absolute;
          right: 8px;
          top: 8px;
          transition: opacity 150ms ease;
        }
        .tile-actions.showMenu {
          opacity: 1;
        }
      `}</style>
    </div>
  );
};
